package org.multiplatform.kotfolio.sections

import androidx.compose.runtime.Composable
import org.multiplatform.kotfolio.components.sectionTitle
import org.multiplatform.kotfolio.models.Section
import org.multiplatform.kotfolio.util.Constants
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import org.jetbrains.compose.web.css.ms
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text
import org.multiplatform.kotfolio.rememberAppState
import org.multiplatform.kotfolio.util.Res

private val AMBER_500 = Color.rgb(245, 158, 11)
private val DARK_BROWN = Color.rgb(44, 25, 16)

data class ServiceSpiel(
    val icon: String,
    val title: String,
    val subtitle: String,
    val description: String,
)

val serviceSpiels = listOf(
    ServiceSpiel(
        Res.Icon.CROSS_PLATFORM_ANDROID,
        "Android & Hybrid Engineering",
        "Native, Vue, and Svelte",
        "Expert in native Android development with over 8 years of experience. Pioneering hybrid solutions by integrating Vue and Svelte web components into native apps via WebView to deliver high-performance, cross-functional mobile experiences.",
    ),
    ServiceSpiel(
        Res.Icon.BACKEND_CORE,
        "Backend & Core Services",
        "Spring Boot, Ktor, PostgreSQL",
        "Building scalable backend architectures using modern Java and Kotlin frameworks. Proficient in API design (Swagger/OpenAPI), JPA/Hibernate, and infrastructure tools like Docker, Redis, and PostgreSQL for robust system ecosystems.",
    ),
    ServiceSpiel(
        Res.Icon.AI_ASSISTED_DEVELOPMENT,
        "AI-Assisted Development",
        "Productivity & Code Quality",
        "Leveraging cutting-edge AI tools to accelerate prototyping, code generation, and refactoring. Focused on improving maintainability and ensuring architectural standards are met while driving rapid feature delivery.",
    ),
    ServiceSpiel(
        Res.Icon.SECURITY_TESTING,
        "Security & Testing",
        "VAPT, Unit & Integration",
        "Implementation of security best practices, including VAPT requirements and RSA encryption. Ensuring system stability through rigorous automated unit and integration testing using tools like Testcontainers and JUnit.",
    ),
    ServiceSpiel(
        Res.Icon.AGILE_CROSS_FUNCTIONAL,
        "Agile & Cross-Functional",
        "Agile, Scrum, Leadership",
        "Extensive experience in daily stand-ups, sprint planning, and refinements. Collaborating closely with product owners and stakeholders to align technical implementations with strategic business goals.",
    ),
    ServiceSpiel(
        Res.Icon.DEPLOYMENT_INFRASTRUCTURE,
        "Deployment & Infrastructure",
        "CI/CD, Azure, Linux",
        "Managing non-production and staging environments to ensure stable integrations. Expertise in CI/CD pipelines, Azure DevOps, and Linux-based flashing/testing environments for end-to-end lifecycle management.",
    ),
)

@Composable
fun serviceSectionV2() {
    val breakpoint = rememberAppState().breakpoint
    val isDesktop = breakpoint >= Breakpoint.MD

    Box(
        modifier = Modifier
            .id(Section.Service.id)
            .fillMaxWidth()
            .maxWidth(Constants.SECTION_WIDTH.px)
            .padding(topBottom = if (isDesktop) 150.px else 80.px)
            .backgroundColor(DARK_BROWN),
        contentAlignment = Alignment.Center,
    ) {
        Column(
            modifier = Modifier.fillMaxWidth(if (isDesktop) 90.percent else 95.percent),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            sectionTitle(
                modifier = Modifier.margin(bottom = if (isDesktop) 100.px else 60.px),
                section = Section.Service,
                alignment = Alignment.CenterHorizontally,
            )

            SimpleGrid(
                modifier = Modifier.fillMaxWidth(),
                numColumns = numColumns(base = 1, md = 2, lg = 3),
            ) {
                serviceSpiels.forEach { spiel ->
                    ServiceSpielCard(spiel)
                }
            }
        }
    }
}

@Composable
private fun ServiceSpielCard(spiel: ServiceSpiel) {
    Box(
        modifier = Modifier
            .padding(all = 16.px)
            .fillMaxSize(),
    ) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(all = 32.px)
                .backgroundColor(Color.rgba(255, 255, 255, 0.03f))
                .border(1.px, org.jetbrains.compose.web.css.LineStyle.Solid, Color.rgba(255, 255, 255, 0.1f))
                .borderRadius(24.px)
                .transition(com.varabyte.kobweb.compose.css.Transition.of("transform", 200.ms), com.varabyte.kobweb.compose.css.Transition.of("background-color", 200.ms)),
        ) {
            Image(
                modifier = Modifier.fillMaxSize()
                    .size(48.px)
                    .objectFit(ObjectFit.Cover)
                    .styleModifier {
                        property("filter", "brightness(0) invert(1)")
                    },
                src = spiel.icon
            )

            P(
                attrs = Modifier
                    .margin(top = 10.px, bottom = 2.px)
                    .fontFamily(Constants.FONT_FAMILY)
                    .fontSize(20.px)
                    .fontWeight(FontWeight.Bold)
                    .color(Colors.White)
                    .toAttrs(),
            ) { Text(spiel.title) }

            P(
                attrs = Modifier
                    .margin(top = 0.px, bottom = 20.px)
                    .fontFamily(Constants.FONT_FAMILY)
                    .fontSize(14.px)
                    .fontWeight(FontWeight.Medium)
                    .color(AMBER_500)
                    .toAttrs(),
            ) { Text(spiel.subtitle) }

            P(
                attrs = Modifier
                    .margin(top = 0.px, bottom = 0.px)
                    .fontFamily(Constants.FONT_FAMILY)
                    .fontSize(15.px)
                    .color(Color.rgba(255, 255, 255, 0.7f))
                    .lineHeight(1.6)
                    .toAttrs(),
            ) { Text(spiel.description) }
        }
    }
}
